'use client';

import { clsx } from 'clsx';
import { PropsWithChildren, ComponentProps, ReactNode } from 'react';
import {
  Disclosure as DisclosureComp,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react';

import { Icons, Typography } from '@monoimin/atoms';

type AdditionalProps = {
  'data-testid'?: string;
};

export type DisclosureProps = PropsWithChildren<{
  disclosureProps: ComponentProps<typeof DisclosureComp> & AdditionalProps;
  buttonProps: ComponentProps<typeof DisclosureButton> & AdditionalProps;
  panelProps: ComponentProps<typeof DisclosurePanel> & AdditionalProps;
  titleProps: ComponentProps<typeof Typography> & {
    open?: string;
    close?: string;
  };
  iconProps?: ComponentProps<typeof Icons>;
}>;

export const Disclosure = ({
  children,
  disclosureProps,
  buttonProps,
  panelProps,
  titleProps,
  iconProps,
}: DisclosureProps) => {
  const showStateDependentTitle = Boolean(
    titleProps?.open && titleProps?.close
  );

  return (
    <DisclosureComp {...disclosureProps}>
      {({ open }) => (
        <>
          <DisclosureButton {...buttonProps}>
            {showStateDependentTitle ? (
              <Typography {...titleProps}>
                {open ? titleProps.open : titleProps.close}
              </Typography>
            ) : (
              <Typography {...titleProps} />
            )}
            {iconProps && <Icons {...iconProps} />}
          </DisclosureButton>
          <DisclosurePanel {...panelProps}>{children}</DisclosurePanel>
        </>
      )}
    </DisclosureComp>
  );
};

type DisclosureClassNames = {
  disclosure?: string;
  button?: string;
  title?: string;
  icon?: string;
  panel?: string;
};

const createProps = ({
  classNames,
  titleProps,
}: {
  classNames?: DisclosureClassNames;
  titleProps: Omit<
    DisclosureProps['titleProps'],
    'as' | 'className' | 'children'
  > & {
    children: ReactNode;
  };
}): DisclosureProps => {
  return {
    disclosureProps: {
      as: 'div',
      className: clsx('text-left border-b border-line', classNames?.disclosure),
    },
    buttonProps: {
      className: clsx('flex items-center pb-2 text-left', classNames?.button),
    },
    titleProps: {
      as: 'span',
      className: clsx('body text-link', classNames?.title),
      ...titleProps,
    },
    iconProps: {
      icon: 'dropdown',
      isInteractive: true,
      className: clsx('ui-open:rotate-180 ml-2 duration-100', classNames?.icon),
    },
    panelProps: {
      as: 'div',
      unmount: false,
      className: clsx('py-4', classNames?.panel),
    },
  };
};
//TODO нужно будет что то другое придумать вместо этого подхода, рантайм ошибку выдает
Disclosure.createProps = createProps;

export default Disclosure;
